import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@tyro/i18n';
import { useMemo } from 'react';
import {
  type ReturnTypeFromUseDocumentationSearch,
  useDocumentationSearch,
} from '../../api/search';
import { AncestorBreadcrumbs } from '../ancestor-breadcrumbs';
import { PageKindIcon } from '../page-kind-icon';
import { SearchSnippet } from './search-snippet';

interface SearchResultsPanelProps {
  query: string;
  onSelectSpace: (spaceSlug: string) => void;
  onNavigatePage: (spaceSlug: string, pageSlug: string) => void;
}

export function SearchResultsPanel({
  query,
  onSelectSpace,
  onNavigatePage,
}: SearchResultsPanelProps) {
  const { t } = useTranslation(['documentation']);
  const { data: matches } = useDocumentationSearch(query);

  const grouped = useMemo(() => {
    if (!matches) return null;
    const map = new Map<string, ReturnTypeFromUseDocumentationSearch[]>();
    for (const match of matches) {
      const existing = map.get(match.spaceName);
      if (existing) {
        existing.push(match);
      } else {
        map.set(match.spaceName, [match]);
      }
    }
    return { total: matches.length, groups: Array.from(map.entries()) };
  }, [matches]);

  if (!grouped) return null;

  if (grouped.total === 0) {
    return (
      <Stack gap={3} sx={{ flex: 1, minHeight: 0 }}>
        <Stack gap={0.5}>
          <Typography variant="subtitle1" component="h3">
            {t('documentation:search.results')}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 4, textAlign: 'center' }}
        >
          {t('documentation:search.noResults', { value: query })}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack gap={2} sx={{ flex: 1, minHeight: 0 }}>
      <Stack gap={0.5} sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle1" component="h3">
          {t('documentation:search.results')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('documentation:search.resultsDescription', {
            count: grouped.total,
            value: query,
          })}
        </Typography>
      </Stack>

      <Stack gap={3} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {grouped.groups.map(([spaceName, pages]) => (
          <Stack key={spaceName} gap={1}>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <Typography
                variant="subtitle2"
                component="h4"
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={() => onSelectSpace(pages[0].spaceSlug)}
              >
                {spaceName}
              </Typography>
              <Chip
                label={pages.length}
                variant="soft"
                color="primary"
                size="small"
                sx={{ height: 18, minWidth: 24, fontSize: 11 }}
              />
            </Stack>
            <Stack gap={1}>
              {pages.map((page) => {
                return (
                  <Card
                    key={page.pageId}
                    variant="outlined"
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() =>
                      onNavigatePage(page.spaceSlug, page.pageSlug)
                    }
                  >
                    <AncestorBreadcrumbs titles={page.ancestorTitles} />
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ minWidth: 0 }}
                    >
                      <PageKindIcon entityType={page.entityType} withTooltip />
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, minWidth: 0 }}
                      >
                        {page.title}
                      </Typography>
                    </Stack>
                    {page.snippet && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        <SearchSnippet snippet={page.snippet} />
                      </Typography>
                    )}
                  </Card>
                );
              })}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}
